import {
  createAttribute,
  removeAttribute,
  updateAttribute,
} from 'actions/attribute/';
import {
  getConditions,
  createCondition,
  updateCondition,
} from 'actions/condition/';
import { Tab } from 'components/Tab/';
import { getTags } from 'actions/tag/';
import { showModal } from 'actions/modals/';
import { getDefault } from 'actions/status/';
import { useParams } from 'react-router-dom';
import { showConfirm } from 'actions/confirm/';
import React, { useMemo, useEffect } from 'react';
import { getCategories } from 'actions/category/';
import { getWarehouses } from 'actions/warehouses/';
import { GOOD, INVENTORY } from 'constants/inventory';
import { ITEM_QUANTITY_MODAL } from 'constants/modal';
import { useDispatch, useSelector } from 'react-redux';
import { Form, History, Attribute, Condition } from 'components/Item/';
import { getItems, createItem, updateItem, getHistory } from 'actions/item/';
import { DELETE_ITEM, DELETE_ATTRIBUTE, DELETE_CONDITION } from 'actions/types';

function Item({ isOnBoarding = false, setShowAddUI = null }) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { data: defaultStatus } = useSelector(
    ({ defaultStatus }) => defaultStatus,
  );
  const good = useMemo(() => {
    return defaultStatus?.find(function (s) {
      return s.status_name === GOOD;
    });
  }, [defaultStatus]);
  const user = useSelector(({ user }) => user);
  const { data: tags } = useSelector(({ tag }) => tag);
  const company = useSelector(({ company }) => company);
  const companyId = company && company.id;
  const { data: items } = useSelector(({ item }) => item);
  const { data: upload } = useSelector(({ upload }) => upload);
  const itemHistory = useSelector(({ itemHistory }) => itemHistory);
  const { data: conditions } = useSelector(({ condition }) => condition);
  const { data: warehouses, isLoading: isLoadingWarehouses } = useSelector(
    ({ warehouses }) => warehouses,
  );
  const { data: categories } = useSelector(({ categories }) => categories);
  const orderTags = useMemo(() => {
    return tags.filter(function (tag) {
      return tag?.tagtype === INVENTORY;
    });
  }, [tags]);
  const settings = useMemo(() => {
    return {
      units: company?.other_details?.quantity_units,
      maxQuantity: company?.package_details?.max_quantity,
    };
  }, [company]);

  const item = useMemo(() => {
    const data = items.find(function (item) {
      return item.id === id;
    });
    if (data) {
      const attributes = data?.attributes?.filter(function (attribute) {
        return !attribute.is_deleted;
      });
      return {
        attributes,
        quantity: data.quantity,
        name: unescape(data.item_name),
      };
    }
    return { name: '', quantity: 0, attributes: [] };
  }, [id, items]);

  const statuses = useMemo(() => {
    const items = defaultStatus?.map(function (status) {
      const { id, status_name: name } = status;
      return { id, name, quantity: 0 };
    });
    return items;
  }, [defaultStatus]);

  const condition = useMemo(() => {
    const conditionId = conditions?.length > 0 ? conditions[0].id : '';
    if (conditionId) {
      const data = conditions[0].conditions;
      const items = Object.entries(data).map(function ([id, quantity]) {
        const name = getCondition(id);
        return { id, name, quantity };
      });
      return { items, conditionId };
    }
    return { items: statuses, conditionId };
  }, [statuses, conditions]);

  function getCondition(id) {
    const data = defaultStatus.find(function (status) {
      return status.id === id;
    });
    if (data) {
      return data.status_name;
    }
    return '';
  }

  /**
   * @param {{
   * payload: FormData
   * uploaded: boolean
   * deleting: boolean
   * }} param
   */
  function onSubmit({ payload, uploaded = false, deleting = false }) {
    const { company_id: companyId } = user;
    if (id) {
      const action = updateItem({ id, payload, deleting, uploaded });
      if (deleting) {
        dispatch(
          showConfirm({
            options: {
              deleting: true,
            },
            type: DELETE_ITEM,
            onConfirm: function () {
              dispatch(action);
            },
            caption: 'Are you sure you want to delete item ?',
          }),
        );
      } else {
        dispatch(action);
      }
    } else {
      payload.append('company_id', companyId);
      dispatch(createItem(payload, companyId));
    }
  }

  function onDeleteAttribute(payload) {
    dispatch(
      showConfirm({
        options: {
          deleting: true,
        },
        type: DELETE_ATTRIBUTE,
        onConfirm: function () {
          dispatch(removeAttribute(payload));
        },
        caption: `Are you sure you want to delete ${payload.attributes.length} attribute(s) ?`,
      }),
    );
  }

  function onSubmitCondition({ condition, updating, deleting }) {
    if (updating) {
      dispatch(updateCondition(condition));
    } else if (deleting) {
      dispatch(
        showConfirm({
          options: {
            deleting: true,
          },
          type: DELETE_CONDITION,
          onConfirm: function () {
            dispatch(updateCondition(condition, true));
          },
          caption: `Are you sure you want to delete all ${item.name} condition ?`,
        }),
      );
    } else {
      dispatch(createCondition(condition));
    }
  }

  function onSubmitAttribute({ attributes, isUpdating }) {
    if (isUpdating) {
      dispatch(updateAttribute(attributes));
    } else {
      dispatch(createAttribute({ attributes }));
    }
  }

  const hasCondition = !!condition.conditionId;

  function onShowQuantityModal() {
    const data = {
      id,
      item,
      statuses,
      companyId,
      isOnBoarding,
      hasCondition,
      conditions: condition.items,
      attributes: item.attributes,
      maxQuantity: settings.maxQuantity,
      conditionId: condition.conditionId,
    };
    dispatch(showModal(ITEM_QUANTITY_MODAL, data));
  }

  const tabs = [
    {
      name: 'general',
      caption: 'General Info',
      children: (
        <Form
          good={good}
          items={items}
          upload={upload}
          tags={orderTags}
          onSubmit={onSubmit}
          itemName={item.name}
          warehouses={warehouses}
          categories={categories}
          hasCondition={hasCondition}
          setShowAddUI={setShowAddUI}
          isOnBoarding={isOnBoarding}
          quantityUnits={settings.units}
          maxQuantity={settings.maxQuantity}
          onShowQuantityModal={onShowQuantityModal}
        />
      ),
    },
    {
      name: 'condition',
      caption: 'Condition',
      children: (
        <Condition
          id={id}
          itemName={item.name}
          stock={item.quantity}
          companyId={companyId}
          isOnBoarding={isOnBoarding}
          conditions={condition.items}
          onSubmit={onSubmitCondition}
          attributes={item.attributes}
          conditionId={condition.conditionId}
        />
      ),
      disabled: !id || item.attributes.length > 0,
    },
    {
      name: 'attributes',
      caption: 'Attributes',
      children: (
        <Attribute
          id={id}
          statuses={statuses}
          itemName={item.name}
          stock={item.quantity}
          companyId={companyId}
          isOnBoarding={isOnBoarding}
          attributes={item.attributes}
          onSubmit={onSubmitAttribute}
          onDelete={onDeleteAttribute}
        />
      ),
      disabled: !id || hasCondition,
      title: id
        ? hasCondition
          ? 'Attributes disabled. Delete conditions to use attributes'
          : 'Add attributes'
        : 'Save item to add attributes',
    },
  ];
  if (id) {
    tabs.push({
      name: 'history',
      caption: 'History',
      children: <History itemHistory={itemHistory} />,
    });
  }

  useEffect(() => {
    if (id) {
      dispatch(getConditions(id));
      const { company_id: companyId } = user || {};
      if (companyId) {
        dispatch(getHistory({ inventoryId: id, companyId }));
      }
    }
  }, [id, user, dispatch]);

  useEffect(() => {
    if (user) {
      dispatch(getDefault());
      const { company_id: companyId } = user;
      dispatch(getTags(companyId));
      dispatch(getCategories(companyId));
      dispatch(getWarehouses({ companyId }));
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (user) {
      const { company_id: companyId } = user;
      /**
       * Inventory items are queried by warehouse-id,
       * so delaying this dispatch to wait for warehouses to be
       * loaded first, then dispatch /getItems.
       */
      if (!isLoadingWarehouses && warehouses?.length > 0) {
        dispatch(getItems({ companyId }));
      }
    }
  }, [user, dispatch, warehouses, isLoadingWarehouses]);

  return <Tab options={tabs} />;
}

export default Item;
